type RecordValue = Record<string, unknown>;

const asRecord = (value: unknown): RecordValue | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as RecordValue)
    : null;

export type VerifiedReAuthPayment = {
  orderId: number;
  email: string;
};

export const parseVerifiedReAuthPayment = (
  response: unknown,
  expectedTransactionId: string
): VerifiedReAuthPayment | null => {
  const body = asRecord(asRecord(response)?.body);
  const data = asRecord(body?.data);
  if (
    body?.success !== true ||
    data?.transaction_id !== expectedTransactionId ||
    data?.payment_status !== "succeeded"
  ) {
    return null;
  }

  const orderId = Number(data.order_id);
  const email =
    typeof data.email === "string" ? data.email.trim() : "";
  if (
    !Number.isSafeInteger(orderId) ||
    orderId <= 0 ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  ) {
    return null;
  }

  return {
    orderId,
    email,
  };
};

export type FailedReAuthPayment = {
  reason: string | null;
};

export const parseFailedReAuthPayment = (
  response: unknown,
  expectedTransactionId: string
): FailedReAuthPayment | null => {
  const root = asRecord(response);
  const body = asRecord(root?.body) || root;
  const data = asRecord(body?.data);
  const paymentStatus =
    typeof data?.payment_status === "string"
      ? data.payment_status.trim().toLowerCase()
      : "";
  const transactionStatus =
    typeof data?.transaction_status === "string"
      ? data.transaction_status.trim().toLowerCase()
      : "";

  if (
    data?.transaction_id !== expectedTransactionId ||
    !["failed", "declined"].includes(transactionStatus) ||
    !["failed", "canceled", "cancelled", "declined", "requires_payment_method"].includes(paymentStatus)
  ) {
    return null;
  }

  const reason = typeof data.failure_reason === "string"
    ? data.failure_reason.trim().slice(0, 300)
    : "";
  return { reason: reason || null };
};

export const isSuccessfulEmailResponse = (response: unknown): boolean => {
  const root = asRecord(response);
  const body = asRecord(root?.body);
  return body?.success === true || (body === null && root?.success === true);
};
